type ResponseData = Record<string, unknown> | string | null

export type QueryParamValue = string | number | boolean | undefined | null

export type QueryParams = Record<string, QueryParamValue | QueryParamValue[]>

export class FetchError extends Error {
  response?: {
    data: ResponseData | undefined
    status: number
    statusText: string
    headers: Headers
  }
  request?: Request
  code?: string

  constructor(message: string, response?: Response, data?: ResponseData) {
    super(message)
    this.name = 'FetchError'
    if (response) {
      this.response = {
        data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      }
    }
  }

  static isFetchError(error: unknown): error is FetchError {
    return error instanceof FetchError
  }
}

export type Method = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

interface RequestOptions<TData> {
  url: string
  headers: Record<string, string>
  data?: TData
  params?: QueryParams
}

interface MakeRequestOptions<TData> {
  authToken: string
  data?: TData
  endpoint: string
  baseUrl: string
  method?: Method
}

interface CreateRequestOptions<TData> {
  authToken: string
  data?: TData
  endpoint: string
  baseUrl: string
  method?: Method
}

export const objectToQueryString = (params: QueryParams): string => {
  const parts: string[] = []
  const getEncodedValue = (value: string) =>
    encodeURIComponent(value).replace(/%3A/g, ':').replace(/%2C/g, ',')

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      const encodedKey = encodeURIComponent(key)
      if (Array.isArray(value)) {
        // pass array of values to query string as key=value&key=value&key=value...
        // as was the case before the refactor of the API request infrastructure.
        value.forEach((val) => {
          parts.push(`${encodedKey}=${getEncodedValue(String(val))}`)
        })
      } else {
        const stringValue = String(value)
        const encodedValue = getEncodedValue(stringValue)
        parts.push(`${encodedKey}=${encodedValue}`)
      }
    }
  })
  return parts.join('&')
}

const request = async <TData, TResponse>(
  method: Method,
  { url, headers, data, params }: RequestOptions<TData>
): Promise<TResponse> => {
  try {
    if (process.env.NODE_ENV === 'development' && url.includes('?')) {
      console.warn(
        `⚠️ fetchApi Warning: URL contains query string: "${url}"\n` +
          `Use the 'params' option instead of embedding query strings in the URL.\n` +
          `Example: fetchApi.get(url, { params: { key: value } })\n` +
          `This ensures proper encoding of special characters like colons in timestamps.`
      )
    }

    let finalUrl = url
    if (params) {
      const queryString = objectToQueryString(params)
      if (queryString) {
        finalUrl += `?${queryString}`
      }
    }

    const requestInit: RequestInit = {
      method,
      mode: 'cors',
      // Use 'omit' to match axios default behavior (no credentials sent cross-origin)
      // This prevents unnecessary CORS preflight requests when using Bearer token auth
      credentials: 'omit',
      headers: {
        Accept: '*/*',
        'Accept-Language': 'en-US,en;q=0.9',
        ...headers,
      },
    }

    if (method !== 'GET' && data) {
      requestInit.headers = {
        ...requestInit.headers,
        'Content-Type': 'application/json',
      }
      requestInit.body = JSON.stringify(data)
    }

    const response = await fetch(finalUrl, requestInit)

    let responseData: ResponseData
    const contentType = response.headers.get('content-type')
    const contentLength = response.headers.get('content-length')

    if (contentLength === '0' || response.status === 204) {
      responseData = null
    } else if (contentType?.includes('application/json')) {
      const text = await response.text()
      if (text) {
        try {
          responseData = JSON.parse(text) as ResponseData
        } catch (e) {
          responseData = text
        }
      } else {
        responseData = null
      }
    } else {
      responseData = await response.text()
    }

    if (!response.ok) {
      const errorMessage =
        typeof responseData === 'object' && responseData && 'message' in responseData
          ? String(responseData.message)
          : response.statusText || 'Unknown error'
      const error = new FetchError(`Request failed: ${errorMessage}`, response, responseData)
      throw error
    }

    return responseData as TResponse
  } catch (error) {
    if (error instanceof FetchError) {
      throw error
    }
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      const errorMessage = `Network error: Failed to connect to server at ${url}. This could be due to CORS, network issues, or the server being down.`
      throw new FetchError(errorMessage)
    }

    throw new FetchError(`Unexpected error occurred: ${(error as Error).message}`)
  }
}

export const createRequest = async <TData extends object | undefined, TResponse>(
  options: CreateRequestOptions<TData>
): Promise<TResponse> => {
  const { authToken, data, endpoint, baseUrl, method = 'GET' } = options
  const headers = {
    Authorization: `Bearer ${authToken}`,
  }

  const requestUrl = `${baseUrl}/${endpoint}/`

  return request<TData, TResponse>(method, {
    url: requestUrl,
    headers,
    data: method === 'GET' ? undefined : data,
    params: method === 'GET' && data ? (data as QueryParams) : undefined,
  })
}

export const makeRequest = <TData extends object | undefined, TResponse = unknown>(
  requestOptions: MakeRequestOptions<TData>
): Promise<TResponse> => {
  const { authToken, data, endpoint, baseUrl, method = 'GET' } = requestOptions

  if (method !== 'GET' && method !== 'DELETE' && !data) {
    throw new Error(`Data is required for ${method} requests`)
  }

  return createRequest<TData, TResponse>({
    authToken,
    data,
    endpoint,
    baseUrl,
    method,
  })
}

export const fetchApi = {
  get: async <TResponse = unknown>(
    url: string,
    config?: { headers?: Record<string, string>; params?: QueryParams }
  ): Promise<{ data: TResponse }> => {
    const response = await request<never, TResponse>('GET', {
      url,
      headers: config?.headers || {},
      data: undefined,
      params: config?.params,
    })
    return { data: response }
  },

  post: async <TData = unknown, TResponse = unknown>(
    url: string,
    data?: TData,
    config?: { headers?: Record<string, string>; params?: QueryParams }
  ): Promise<{ data: TResponse }> => {
    const response = await request<TData, TResponse>('POST', {
      url,
      headers: config?.headers || {},
      data,
      params: config?.params,
    })
    return { data: response }
  },

  patch: async <TData = unknown, TResponse = unknown>(
    url: string,
    data?: TData,
    config?: { headers?: Record<string, string>; params?: QueryParams }
  ): Promise<{ data: TResponse }> => {
    const response = await request<TData, TResponse>('PATCH', {
      url,
      headers: config?.headers || {},
      data,
      params: config?.params,
    })
    return { data: response }
  },

  delete: async <TResponse = unknown>(
    url: string,
    config?: { headers?: Record<string, string>; params?: QueryParams }
  ): Promise<{ data: TResponse }> => {
    const response = await request<never, TResponse>('DELETE', {
      url,
      headers: config?.headers || {},
      params: config?.params,
    })
    return { data: response }
  },

  put: async <TData = unknown, TResponse = unknown>(
    url: string,
    data?: TData,
    config?: { headers?: Record<string, string>; params?: QueryParams }
  ): Promise<{ data: TResponse }> => {
    const response = await request<TData, TResponse>('PUT', {
      url,
      headers: config?.headers || {},
      data,
      params: config?.params,
    })
    return { data: response }
  },

  isFetchError: (error: unknown): error is FetchError => FetchError.isFetchError(error),
}

export default fetchApi
